import { Modal, Tabs, useMantineTheme } from "@mantine/core";
import dynamic                          from "next/dynamic";
import Link                             from "next/link";
import { useTranslation }               from "react-i18next";

import { useGetDeviceType } from "_common/hooks/global";
import { shortenNumber }    from "_common/utils";
import BackgroundWrapper    from "_components/atoms/backgroundWrapper";
import Icon                 from "_components/atoms/icon";
import Image                from "_components/atoms/image";
import InputFieldVideos     from "_components/atoms/inputFieldVideos";
import Typography           from "_components/atoms/typography";
import EmojiModal           from "_components/features/chat/userInteractions/emojiModal";
import ItemList             from "_components/organisms/reactionBox/ItemList";
import useStyles            from "_styles/molecules/ImageInfoModal";
import useStylesReaction    from "_styles/organisms/reactionBox";

const ReportFollowModal = dynamic ( () => import ( "_components/molecules/reportFollowModal" ), { ssr: false } );

const component = props => {
	const {
		image,
		handleModalClose,
		modalStatus,
		handleOnCommentChange,
		commentCount,
		addCommentToPost,
		handleTabChange,
		currentTabParams,
		fetchLikes,
		fetchComments,
		activeTab,
		commentInput,
		handleClickModal,
		reachedListEnd,
		listStartCheck,
		followers,
		verticalFeedType,
		updateDeletedPost,
		handleToggleEmoji,
		textInput,
		focusState,
		displayEmoji,
		handleEmojiSelect,
		toggleFocusState
	} = props;

	const { classes: classesReactionBox } = useStylesReaction ();
	const isCelebCheck                    = image?.actorDetails?.isCeleb;

	const { t } = useTranslation ();

	const { classes } = useStyles ( { displayEmoji }, { name: "styles" } );
	const theme       = useMantineTheme ();
	const deviceType  = useGetDeviceType ();
	const isMobile    = deviceType === "mobile";

	return (
		<Modal
			centered
			aria-describedby = "modal-modal-description"
			aria-labelledby = "modal-modal-title"
			className = { classes.modalWrapper }
			classNames = { { content: classes.modalContent } }
			closeOnClickOutside = { true }
			closeOnEscape = { true }
			onClose = { handleModalClose }
			opened = { modalStatus }
			radius = { "lg" }
			styles = { { body: { padding: "0", display: "flex", justifyContent: "center" } } }
			withCloseButton = { false }
		>
			<div className = { classes.imageWrapper }>

				<Icon sameInBothTheme
					className = { classes.crossIcon }
					name = "transparentCross"
					onClick = { handleModalClose }
				/>

				{
					listStartCheck && (
						<Icon sameInBothTheme
							className = { classes.prevIcon }
							name = "prevIcon"
							onClick = { () => {
								handleClickModal ( "back" );
							} }
						/>

					)
				}

				{
					reachedListEnd && (
						<Icon sameInBothTheme
							className = { classes.nextIcon }
							name = "nextIcon"
							onClick = { () => {
								handleClickModal ( "next" );
							} }
						/>
					)
				}

				<div className = { classes.mainWrapper }>

					<Image
						className = { classes.image }
						src = { image?.media?.[0]?.href }
					/>

					<div className = { classes.contentBoxWrapper }>
						<div className = { classes.authorDetailsWrapper }>
							<Link href = { `/profile/${ image.actorDetails.id }` }>
								<Image className = { classes.profileImage }
									src = { image.actorDetails.photo }
								/>
							</Link>

							<div className = { classes.nameWrapper }>
								<Link href = { `/profile/${ image.actorDetails.id }` }>
									<div className = { classes.headingWrapper }>
										<Typography
											className = { classes.name }
											title = { image.actorDetails.name }
										/>

										{isCelebCheck ? (
											<Icon sameInBothTheme
												className = { classes.verifiedIcon }
												name = "verifiedProfile"
											/>
										) : null}
									</div>
								</Link>

								<span className = { classes.followersCount }>
									{shortenNumber ( followers )}

									{" "}
									Followers
								</span>
							</div>

							<div className = { classes.menuIconContainer }>

								<ReportFollowModal
									feedId = { image.id }
									feedType = { image?.feedType?.[0] }
									from = "image-modal" // for setting zIndex property of modal
									handleModalClose = { handleModalClose }
									isFollowing = { image.actorDetails.isFollowing }
									updateDeletedPost = { updateDeletedPost }
									userId = { image.actorDetails.id }
									userName = { image.actorDetails.name }
									verticalFeedType = { verticalFeedType }
								/>

							</div>

						</div>

						<div className = { classes.dialogContent }>
							<div className = { classesReactionBox.appBar }
								style = { { position: "static" } }
							>

								<Tabs
									// className      = { classesReactionBox.rootTabs }
									onChange = { handleTabChange }
									styles = { {
										root: {
											width: "100%"
										},
										tabsList: {
											width          : "100%",
											display        : "flex",
											borderBottom   : theme.other.darkMode ? "none" : `1px solid ${ theme.other.colors.border.primary }`,
											justifyContent : "space-around"
										},
										tabLabel: {
											color: "inherit"
										},
										tab: {
											flex         : "1",
											padding      : "1.5rem 0 1.5rem 0",
											borderRadius : "0 !important",
											marginBottom : "unset",
											background   : theme.other.colors.background.tertiary,
											borderBottom : "none",
											":hover"     : {
												background: theme.other.colors.background.tertiary
											},
											":active": {
												borderBottom: "0"
											},
											":focus": {
												borderBottom : "0",
												marginBottom : "0 !important"

											},
											color            : theme.other.colors.text.secondary,
											"&[data-active]" : {
												position   : "relative",
												fontWeight : "500",
												color      : `${ theme.other.paletteNew.primaryDarkBlue } !important`,
												border     : "0",
												":after"   : {
													content      : `'${ "" }'`,
													width        : "100%",
													height       : "3px",
													position     : "absolute",
													background   : "#126bd5",
													bottom       : "0",
													borderRadius : "10px 10px 0 0"
												}
											}
										},
										panel: {
											height : isMobile ? "50vh" : "calc( 100vh - 115px )",
											width  : "100%"
										}
									} }
									value = { activeTab }
								>
									<Tabs.List>

										<Tabs.Tab
											value = "0"
										>
											<Typography className = { activeTab !== 1 ? classesReactionBox.activeText : classesReactionBox.unActiveText }
												color = { activeTab === 1 ? "#757575" : theme.other.paletteNew.primaryDarkBlue }
												title = { t ( "reactionbox_comments" ) + ` (${ commentCount })` }
												variant = "body1"
											/>
										</Tabs.Tab>

										<Tabs.Tab
											value = "1"
										>
											<Typography className = { activeTab !== 0 ? classesReactionBox.activeText : classesReactionBox.unActiveText }
												color = { activeTab === 0 ? "#757575" : theme.other.paletteNew.primaryDarkBlue }
												title = { t ( "reactionbox_likes" ) + ` (${ image.reactions[0].count })` }
												variant = "body1"

											/>
										</Tabs.Tab>
									</Tabs.List>
								</Tabs>

							</div>

							{
								activeTab === 0 && (
									<>
										<ItemList
											activeTab = { activeTab }
											dataList = { currentTabParams.data }
											fetchComments = { fetchComments }
											isDepleted = { currentTabParams.isDepleted }
											isFetching = { currentTabParams.isFetching }
											pageNo = { currentTabParams.pageNo }
										/>

										<BackgroundWrapper className = { classes.emojiModalParent }
											type = "secondary"
										>
											{
												displayEmoji ? (
													<div className = { classes.emojiModalWrapper }>
														<EmojiModal handleEmojiSelect = { handleEmojiSelect } />
													</div>
												) : null
											}
										</BackgroundWrapper>

										<div className = { classes.inputComment }>
											<InputFieldVideos
												commentInput = { commentInput }
												displayEmoji = { displayEmoji }
												focusState = { focusState }
												handleEmojiSelect = { handleEmojiSelect }
												handleOnChange = { handleOnCommentChange }
												handleSendClick = { addCommentToPost }
												handleToggleEmoji = { handleToggleEmoji }
												textInput = { textInput }
												toggleFocusState = { toggleFocusState }
											/>
										</div>
									</>
								)
							}

							{
								activeTab === 1 && (
									<ItemList
										activeTab = { activeTab }
										dataList = { currentTabParams.data }
										fetchLikes = { fetchLikes }
										isDepleted = { currentTabParams.isDepleted }
										isFetching = { currentTabParams.isFetching }
										pageNo = { currentTabParams.pageNo }
									/>
								)
							}
						</div>

					</div>
				</div>
			</div>

		</Modal>

	);
};

export default component;
